/**
 * BreakAssistant — schedule-driven break prompts, everywhere in the agent
 * portal (mounted once in AgentLayout).
 *
 * Driven by the published WFM schedule + the agent's live status, ticking
 * every second:
 *
 *   upcoming   break in ≤ 5 min        → "Break in 04:12 — wrap up your task"
 *   on hold    break due during a call → "Break on hold — you're on a call;
 *                                         full 15 min starts when it ends"
 *   ready      break due, agent free   → "Time for your break" [Start break]
 *              (after a call: "Call finished — your full break starts now")
 *   on break   the portal is locked behind a dark overlay; one card shows
 *              the timer (countdown for a scheduled break, red "+02:13" once
 *              it's over, count-up for an unscheduled one) and the only
 *              control: [End break & go Available]
 *
 * Break deferral itself is computed server-side (applyBreakDeferral) so the
 * moved break also counts correctly in adherence; the shared live feed
 * refreshes ~1.5 s after each status change, which is what makes the break
 * snap to "starts now" the moment the call ends.
 */
import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react'
import { AlarmClock, Coffee, PhoneCall, Play, X, Minus } from 'lucide-react'
import { useAgentStore, type UserStatusOption } from '../../store/agentStore.js'
import { getSocket } from '../../socket/client.js'
import { getSelectedTimezone } from '../../utils/timezone.js'
import { LIVE_KEYFRAMES, LiveBar, CONTACT_STATES, blockLength, clock, isBreakCategory, useNow } from './AdherenceWidget.js'
import { useWfmLiveFeed, type LiveBlock } from './wfm-live-store.js'
import { fmtTime } from './wfm-shared.js'

const keyOf = (b: LiveBlock): string => `${b.scheduledStart ?? b.start}|${b.activity}`

function startBreak(activity: string): void {
  const { breakCodes, setUserStatus } = useAgentStore.getState()
  const option: UserStatusOption =
    breakCodes.find((c) => c.text.trim().toLowerCase() === activity.trim().toLowerCase())
    ?? breakCodes.find((c) => c.key !== 'login' && c.key !== 'available')
    ?? { key: 'wfm-break', text: activity }
  setUserStatus(option)
  getSocket().emit('agent:status', { event: 'pause', reason: option.text })
}

function goAvailable(): void {
  useAgentStore.getState().setUserStatus({ key: 'available', text: 'Available' })
  getSocket().emit('agent:status', { event: 'unpause' })
}

export default function BreakAssistant({ onCall }: { onCall: boolean }): JSX.Element | null {
  const [tz] = useState(getSelectedTimezone)
  const { data, unavailable } = useWfmLiveFeed(tz)
  const now = useNow(1000)
  const live = (useAgentStore((s) => s.status) ?? data?.currentState ?? 'OFFLINE').toUpperCase()
  const userKey = useAgentStore((s) => s.userStatus.key)
  const userText = useAgentStore((s) => s.userStatus.text)
  const breakSince = useAgentStore((s) => {
    const last = s.sessionHistory[s.sessionHistory.length - 1]
    return last && last.key === s.userStatus.key ? last.time.getTime() : s.since ? Date.parse(s.since) : null
  })
  const [snoozed, setSnoozed] = useState<Record<string, number>>({})
  const [dismissed, setDismissed] = useState<Record<string, true>>({})
  const [minimised, setMinimised] = useState(false)

  const breaks = useMemo(() => (data?.today?.blocks ?? []).filter((b) => isBreakCategory(b.category)), [data])
  const current = breaks.find((b) => Date.parse(b.start) <= now && now < Date.parse(b.end)) ?? null
  const last = [...breaks].reverse().find((b) => Date.parse(b.end) <= now) ?? null
  const next = breaks.find((b) => Date.parse(b.start) > now) ?? null

  const contact = onCall || CONTACT_STATES.has(live)
  // On break = a break status was chosen (header / Start break) or the server
  // put the agent ON_BREAK. Live PAUSED alone is NOT a break: the Ready/Idle
  // toggle pauses too.
  const away = (userKey !== 'available' && userKey !== 'login') || live === 'ON_BREAK'

  // Restore the full card when the situation changes.
  const situation = current ? `${keyOf(current)}|${contact}|${away}` : 'none'
  useEffect(() => { setMinimised(false) }, [situation])

  if (away) return <OnBreak label={current?.activity ?? userText} now={now} since={breakSince} scheduled={current} lastScheduled={last} tz={tz} />
  if (unavailable || !data || live === 'OFFLINE') return null

  let card: ReactNode = null
  if (current && contact) {
    const scheduled = Date.parse(current.scheduledStart ?? current.start)
    card = (
      <Card tone="amber" Icon={PhoneCall} title="Break on hold — you're on a call" minimised={minimised} onMinimise={() => setMinimised(true)} onRestore={() => setMinimised(false)} pill={`Break on hold · ${clock(blockLength(current))} reserved`}>
        <p>Your <b>{clock(blockLength(current))}</b> {current.activity.toLowerCase()} starts as soon as this call ends — you won&apos;t lose any of it.</p>
        <p className="mt-1 text-[12px] opacity-80">Scheduled {fmtTime(new Date(scheduled).toISOString(), tz)} · held for <b className="tabular-nums">{clock(now - scheduled)}</b></p>
      </Card>
    )
  } else if (current) {
    const k = keyOf(current)
    const wasDeferred = (current.deferredMin ?? 0) > 0
    if (!(snoozed[k] && snoozed[k]! > now)) {
      card = (
        <Card
          tone="brand" Icon={Coffee}
          title={wasDeferred ? 'Call finished — your break starts now' : `Time for your ${current.activity.toLowerCase()}`}
          minimised={minimised} onMinimise={() => setMinimised(true)} onRestore={() => setMinimised(false)}
          pill={`${current.activity} · ${clock(Date.parse(current.end) - now)} left`}
          actions={<>
            <button type="button" onClick={() => setSnoozed({ ...snoozed, [k]: now + 2 * 60_000 })} className="h-8 rounded-[3px] px-3 text-[12px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50">Remind me in 2 min</button>
            <button type="button" onClick={() => startBreak(current.activity)} className="inline-flex h-8 items-center gap-1.5 rounded-[3px] bg-[#3366FF] px-3 text-[12px] font-semibold text-white shadow-[0_4px_12px_-4px_rgba(51,102,255,0.6)] hover:bg-[#2B57E0]"><Play className="h-3.5 w-3.5" />Start break</button>
          </>}
        >
          <p>
            {wasDeferred
              ? <>Moved {current.deferredMin} min later because of your call — you still get the full <b>{clock(blockLength(current))}</b>, until <b>{fmtTime(current.end, tz)}</b>.</>
              : <>Scheduled {fmtTime(current.start, tz)} – {fmtTime(current.end, tz)}. <b className="tabular-nums">{clock(Date.parse(current.end) - now)}</b> left.</>}
          </p>
        </Card>
      )
    }
  } else if (next && !contact && Date.parse(next.start) - now <= 5 * 60_000 && !dismissed[keyOf(next)]) {
    const k = keyOf(next)
    card = (
      <Card tone="soft" Icon={AlarmClock} title={`${next.activity} in ${clock(Date.parse(next.start) - now)}`}
        onClose={() => setDismissed({ ...dismissed, [k]: true })}
      >
        <p>At {fmtTime(next.start, tz)} for {clock(blockLength(next))}. Wrap up what you&apos;re doing. If a call runs over, your break waits for it.</p>
      </Card>
    )
  }

  if (!card) return null
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[950] w-[360px] max-w-[calc(100vw-2rem)]">
      <style>{LIVE_KEYFRAMES + '@keyframes wfm-pop{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>
      <div key={situation} className="pointer-events-auto" style={{ animation: 'wfm-pop 220ms ease-out' }}>{card}</div>
    </div>
  )
}

/**
 * While on break the portal is visible but locked behind a dark overlay; the
 * only control is this card: the break timer and "End break & go Available".
 * Scheduled breaks count down (and turn red once over); unscheduled ones
 * count up.
 */
function OnBreak({
  label, now, since, scheduled, lastScheduled, tz,
}: {
  label: string
  now: number
  since: number | null
  scheduled: LiveBlock | null
  lastScheduled: LiveBlock | null
  tz: string
}): JSX.Element {
  const cardRef = useRef<HTMLDivElement>(null)
  // The overlay stops the mouse; this stops the keyboard (Tab/Enter/Space)
  // from reaching anything behind it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (cardRef.current?.contains(e.target as Node)) {
        if (e.key === 'Tab') e.preventDefault()
        return
      }
      e.preventDefault()
      e.stopPropagation()
      cardRef.current?.querySelector('button')?.focus()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])
  const elapsed = since ? now - since : 0
  const left = scheduled ? Date.parse(scheduled.end) - now : null
  const over = !scheduled && lastScheduled && now - Date.parse(lastScheduled.end) < 60 * 60_000 ? now - Date.parse(lastScheduled.end) : null
  const ending = left !== null && left <= 60_000
  const tone = over !== null ? 'red' : ending ? 'amber' : 'soft'
  const t = TONES[tone]
  return (
    <>
      <style>{LIVE_KEYFRAMES + '@keyframes wfm-fade{from{opacity:0}to{opacity:1}}'}</style>
      {/* Blocks every click/scroll/keyboard target underneath; content stays visible. */}
      <div className="fixed inset-0 z-[2000] bg-slate-950/60 backdrop-blur-[1px]" style={{ animation: 'wfm-fade 200ms ease-out' }} aria-hidden="true" onMouseDown={(e) => e.preventDefault()} />
      <div ref={cardRef} className="fixed bottom-4 right-4 z-[2001] w-[360px] max-w-[calc(100vw-2rem)]" style={{ animation: 'wfm-pop 220ms ease-out' }} role="dialog" aria-modal="true" aria-label="On break">
        <style>{'@keyframes wfm-pop{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>
        <section className={`relative overflow-hidden rounded-[3px] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] ring-1 ring-inset ${t.wrap}`}>
          <span className={`absolute inset-y-0 left-0 w-[3px] ${t.bar}`} />
          <div className="px-5 py-4">
            <div className="flex items-center gap-2.5">
              <span className={`flex h-9 w-9 items-center justify-center rounded-[3px] ${t.icon}`}><Coffee className="h-[18px] w-[18px]" /></span>
              <div className="min-w-0">
                <p className={`truncate text-[14px] font-semibold ${t.title}`}>On {label.toLowerCase() === 'break' ? 'break' : label}</p>
                <p className="text-[12px] text-slate-500">
                  {over !== null ? 'Your scheduled break is over' : scheduled ? `Scheduled until ${fmtTime(scheduled.end, tz)}` : 'Portal is locked while you are on break'}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <p className={`text-[34px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${over !== null ? 'text-rose-700' : 'text-slate-900'}`}>
                  {left !== null ? clock(Math.max(0, left)) : over !== null ? `+${clock(over)}` : clock(elapsed)}
                </p>
                <p className="mt-1 text-[11.5px] text-slate-400">{left !== null ? 'remaining' : over !== null ? 'over your break' : 'on break'}</p>
              </div>
              {(left !== null || over !== null) && since && <p className="text-[11.5px] tabular-nums text-slate-400">on break {clock(elapsed)}</p>}
            </div>
            {scheduled && (
              <div className="mt-3"><LiveBar pct={((now - Date.parse(scheduled.start)) / blockLength(scheduled)) * 100} mode="rest" height={8} /></div>
            )}
            <button
              type="button"
              autoFocus
              onClick={goAvailable}
              className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-[3px] bg-emerald-600 text-[13px] font-semibold text-white shadow-[0_6px_16px_-6px_rgba(16,185,129,0.7)] transition-colors hover:bg-emerald-700"
            >
              <Play className="h-4 w-4" /> End break &amp; go Available
            </button>
          </div>
        </section>
      </div>
    </>
  )
}

const TONES = {
  brand: { wrap: 'bg-white ring-[#3366FF]/30', icon: 'bg-blue-50 text-[#3366FF]', bar: 'bg-[#3366FF]', text: 'text-slate-700', title: 'text-slate-900' },
  amber: { wrap: 'bg-amber-50 ring-amber-300', icon: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500', text: 'text-amber-900', title: 'text-amber-950' },
  red: { wrap: 'bg-rose-50 ring-rose-300', icon: 'bg-rose-100 text-rose-700', bar: 'bg-rose-500', text: 'text-rose-900', title: 'text-rose-950' },
  soft: { wrap: 'bg-white ring-slate-200', icon: 'bg-slate-100 text-slate-600', bar: 'bg-slate-400', text: 'text-slate-700', title: 'text-slate-900' },
} as const

function Card({
  tone, Icon, title, children, actions, minimised, onMinimise, onRestore, onClose, pill,
}: {
  tone: keyof typeof TONES
  Icon: typeof Coffee
  title: string
  children: ReactNode
  actions?: ReactNode
  minimised?: boolean
  onMinimise?: () => void
  onRestore?: () => void
  onClose?: () => void
  pill?: string
}): JSX.Element {
  const t = TONES[tone]
  if (minimised && pill) {
    return (
      <button type="button" onClick={onRestore} className={`inline-flex items-center gap-2 rounded-[3px] px-3 py-2 text-[12.5px] font-semibold shadow-[0_8px_24px_-8px_rgba(15,23,42,0.3)] ring-1 ring-inset ${t.wrap} ${t.title}`}>
        <Icon className="h-4 w-4" /><span className="tabular-nums">{pill}</span>
      </button>
    )
  }
  return (
    <section className={`relative overflow-hidden rounded-[3px] shadow-[0_16px_40px_-12px_rgba(15,23,42,0.35)] ring-1 ring-inset ${t.wrap}`} role="status" aria-live="polite">
      <span className={`absolute inset-y-0 left-0 w-[3px] ${t.bar}`} />
      <div className="flex items-start gap-3 py-3.5 pl-4 pr-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] ${t.icon}`}><Icon className="h-[18px] w-[18px]" /></span>
        <div className={`min-w-0 flex-1 text-[13px] leading-relaxed ${t.text}`}>
          <p className={`mb-0.5 text-[14px] font-semibold ${t.title}`}>{title}</p>
          {children}
          {actions && <div className="mt-3 flex flex-wrap justify-end gap-2">{actions}</div>}
        </div>
        {(onMinimise || onClose) && (
          <button type="button" onClick={onClose ?? onMinimise} className="rounded-[3px] p-1 opacity-60 transition-opacity hover:opacity-100" aria-label={onClose ? 'Dismiss' : 'Minimise'}>
            {onClose ? <X className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
          </button>
        )}
      </div>
    </section>
  )
}
